<script setup lang="ts">
// An encode's bitrate ladder (ladder.ts): renditions below its own
// picture, for an adaptive HLS push.
import { computed } from 'vue'
import type { Video } from '../../api'
import { maxRungs, nextRung, rungHeights } from '../../ladder'

const video = defineModel<Video>({ required: true })
const ladder = computed(() => video.value.ladder ?? [])
const next = computed(() => nextRung(ladder.value, video.value.height ?? 0, Number(video.value.bitrate_k) || 0))
// the heights a rung may take: under the one above it
const heightsFor = (i: number) => {
    const above = i === 0 ? video.value.height || Infinity : (ladder.value[i - 1]?.height ?? Infinity)
    return rungHeights.filter((h) => h < above)
}
function add() {
    if (next.value) video.value.ladder = [...ladder.value, next.value]
}
function remove(i: number) {
    const rest = ladder.value.filter((_, j) => j !== i)
    video.value.ladder = rest.length ? rest : undefined
}
</script>

<template>
    <details :open="ladder.length > 0" data-test="ladder">
        <summary>Ladder (adaptive HLS){{ ladder.length ? ` · ${ladder.length + 1} renditions` : '' }}</summary>
        <div class="mb-1.5 text-xs text-fg-2">
            Top: this encode's own picture ({{ video.height ? `${video.height}p` : "the source's size" }},
            {{ video.bitrate_k }} kb/s)
        </div>
        <div v-for="(r, i) in ladder" :key="i" class="mb-1.5 flex items-center gap-1.5" data-test="rung">
            <select v-model.number="r.height" class="w-24" aria-label="height">
                <option v-for="h in heightsFor(i)" :key="h" :value="h">{{ h }}p</option>
                <option v-if="!heightsFor(i).includes(r.height)" :value="r.height">{{ r.height }}p</option>
            </select>
            <input v-model.number="r.bitrate_k" type="number" min="100" step="50" class="w-24" aria-label="kb/s" />
            <span class="text-xs text-fg-3">kb/s</span>
            <button type="button" class="btn-ghost text-xs" title="Remove the rendition" @click="remove(i)">✕</button>
        </div>
        <button
            type="button"
            class="btn-ghost text-xs"
            :disabled="!next"
            data-test="rung-add"
            :title="ladder.length >= maxRungs ? `at most ${maxRungs} below the top` : ''"
            @click="add"
        >
            + rendition
        </button>
        <p class="text-xs text-fg-3">
            Each rendition is scaled from this encode's picture and encoded beside it with the same settings, so all
            change at the same frames and a player can switch between them. An HLS push with <i>adaptive</i> sends them
            all under one master playlist, for a CDN; every other destination of this encode sends the top one. Each
            rendition costs its own encoding (CPU or GPU).
        </p>
    </details>
</template>
